import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BlankCard,
  Breadcrumbs,
  Button,
  DocStepper,
  FileDropZone,
  SortableTable,
  TextField,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import { MA_DUE_DILIGENCE } from '../../core-utils/routes';
import { useNavigate } from 'react-router-dom';
import { CSSProperties } from '@mui/material/styles';
import {
  AddProjectTabProps,
  extractCategory,
  formatRowsFromCategoryData,
} from '../../core-utils/helper-utils/recommendedAppsHelper';
import ReportCreateNewLabelField from '../../_components/atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { RECOMMENDED_APPS } from '../../core-utils/messages/RecommendedAppConstants';
import InfoBar from '../../_components/atoms/InfoBar';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import { bytesToMB } from '../../core-utils/helper-utils/contractAIQHelper';
import Typography from '@HomaHealth/ui-component-library/lib/_patient-access-components/atoms/Typography';
import { AddProjectProvider, useAddProjectContext } from './AddProjectContext';
import axios from 'axios';
import properties from '../../core-utils/properties';
import Loader from '../../_components/molecules/Loader';
import AutocompleteWithCheckBoxes from '../../_components/atoms/Autocomplete';
import { NoDataVisual } from '../SavingsUtilization';
import AddSubParameter from '../../_components/molecules/AddSubParameter';
import BasicModal from '../../_components/atoms/BasicModal';
import { RowData } from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/SortableTable';
import OnBoardingPopUp from '../../_components/molecules/OnBoardingPopup';

const messages = RECOMMENDED_APPS.MA_DUE_DILIGENCE.ADD_PROJECT;
const useStyles = makeStyles()({
  breadcrumbs: {
    padding: '12px 20px',
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    width: '100%',
  },
  main: {
    width: '1276px',
    padding: '32px 20px 0px 20px',
    marginBottom: '2rem',
  },
  mainContent: {
    marginTop: '2rem',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    width: '100%',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    borderRadius: '0.25rem',
    marginBottom: '1rem',
  },
  uploadFilesGrid: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    margin: '1rem 0',
  },
  uploadedFiles: {
    height: '233px',
    width: '60%',
    background: patientAccessTheme.palette.background.default,
    borderRadius: '4px',
    padding: '18px 12px',
  },
  files: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    height: '136px',
    overflow: 'auto',
    paddingRight: '8px',
    ...scrollBarStyles,
  },
});

//Step 1 - Upload Docket
const UploadFiles: React.FC<{}> = () => {
  const { classes } = useStyles();
  const { uploadedFiles, setUploadedFiles } = useAddProjectContext();
  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Grid className={classes.uploadFilesGrid}>
        {uploadedFiles?.length > 0 && (
          <Grid className={classes.uploadedFiles}>
            <Typography
              fontSize="12px"
              fontType="regular"
              lineHeight="24px"
              textColor={patientAccessTheme.palette.text.lowemphasis}
              margin="0px 0px 16px 0px"
            >
              {`${uploadedFiles?.length} Document${
                uploadedFiles?.length > 1 ? 's' : ''
              } uploaded`}
            </Typography>
            <Grid className={classes.files}>
              {uploadedFiles?.map((item: any, index: number) => {
                return (
                  <UploadedDocument
                    name={item?.name}
                    size={`${bytesToMB(item?.size)} MB`}
                    handleDelete={() => {
                      const updatedItems = [...uploadedFiles];

                      updatedItems.splice(index, 1);

                      setUploadedFiles(updatedItems);
                    }}
                    width="100%"
                    truncateWidth="300px"
                  />
                );
              })}
            </Grid>
          </Grid>
        )}
        <FileDropZone
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          filesSupported={['.pdf']}
          width={uploadedFiles?.length > 0 ? '38%' : '100%'}
          height="233px"
          isMultiple={true}
        />
      </Grid>
      <InfoBar info={messages.UPLOAD_INFO} type="Info" width="100%" />
    </Box>
  );
};
const UploadDocket: React.FC<{}> = () => {
  const { classes } = useStyles();

  const {
    uploadedFiles,
    setEgNid,
    setCurrentStep,
    setStepLoading,
    companyName,
    setCompanyName,
    typeOfPlan,
    setTypeOfPlan,
  } = useAddProjectContext();

  const addDueDiligenceDetails = async () => {
    const formData = new FormData();

    uploadedFiles.forEach((file: any) => {
      formData.append(`files`, file);
    });

    formData.append('companyName', companyName);
    formData.append('typeOfPlan', typeOfPlan);
    setStepLoading(true);
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.addDueDiligenceDetails,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setEgNid(response.data.data?.eg_nid);
      setStepLoading(false);
      setCurrentStep(1);
    } catch (error) {
      setStepLoading(false);
    }
  };

  return (
    <Grid container>
      <Grid className={classes.inputContainer}>
        <ReportCreateNewLabelField
          label={messages.GROUP_NAME}
          component={
            <TextField
              height="36px"
              placeholder={'Enter ' + messages.GROUP_NAME}
              width="250px"
              value={companyName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCompanyName(e.target.value)
              }
            />
          }
          marginBottom="0px"
        />
        <ReportCreateNewLabelField
          label={messages.TYPE_OF_PLAN}
          component={
            <Dropdown
              dropDownOptions={messages.TYPE_OF_PLAN_OPTIONS}
              dropDownValues={messages.TYPE_OF_PLAN_OPTIONS}
              value={typeOfPlan}
              label={'Select ' + messages.TYPE_OF_PLAN}
              height="36px"
              handleChange={(e: any) => setTypeOfPlan(e.target.value as string)}
              selectStyles={{ width: '250px' }}
            />
          }
          marginBottom="0px"
        />
      </Grid>
      <Grid className={classes.inputContainer}>
        <UploadFiles />
      </Grid>
      <Button
        variant="contained"
        label="Upload Files"
        styleProps={{ marginLeft: 'auto' }}
        onClick={addDueDiligenceDetails}
        disabled={companyName === '' || typeOfPlan === ''}
      />
    </Grid>
  );
};

//Step 2 - Select Parameters
const SelectParameters: React.FC<{}> = () => {
  const { classes } = useStyles();
  const {
    companyName,
    typeOfPlan,
    categoryData,
    rows,
    setRows,
    setStepLoading,
    setCurrentStep,
    egNid,
    setOpenEditModal,
    setSelectedSubParameter,
    setSelectedEditOptions,
  } = useAddProjectContext();

  const handleCategoryChange = (event: any, value: any) => {
    const newRows = formatRowsFromCategoryData(categoryData, value);
    setRows(newRows);
  };

  const handleDeleteSubParameter = (subRowId: number, parentRowId: number) => {
    const updateSubrows = (row: RowData): RowData => {
      if (row.id !== parentRowId) {
        return row;
      }
      const filteredSubrows = row.subrows?.filter((sr) => sr.id !== subRowId);
      return {
        ...row,
        subrows: filteredSubrows,
      };
    };
    setRows((prev) => (prev as RowData[]).map(updateSubrows));
  };
  const handleEditSubParameter = (subRow: RowData, parentRowId: number) => {
    setSelectedSubParameter({ name: subRow.name, value: subRow.value });
    setOpenEditModal(true);

    setSelectedEditOptions({ parentRowId, subRowId: subRow.id, mode: 'edit' });
  };
  const handleAddNewSubParameter = (parentRowId: number) => {
    setOpenEditModal(true);

    setSelectedEditOptions({ parentRowId, mode: 'create' });
  };

  const updateDueDiligenceDetails = async () => {
    setStepLoading(true);
    try {
      await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.updateDueDiligenceDetails,
        { eg_nid: egNid, parameters: rows },
      );
      setStepLoading(false);
      setCurrentStep(2);
    } catch (error) {
      setStepLoading(false);
    }
  };
  return (
    <Grid container>
      <Grid className={classes.inputContainer}>
        <ReportCreateNewLabelField
          label={messages.GROUP_NAME}
          component={
            <Typography fontSize="14px" fontType="medium">
              {companyName}
            </Typography>
          }
          marginBottom="0px"
          marginRight="2.5rem"
        />
        <ReportCreateNewLabelField
          label={messages.TYPE_OF_PLAN}
          component={
            <Typography fontSize="14px" fontType="medium">
              {typeOfPlan}
            </Typography>
          }
          marginBottom="0px"
        />
      </Grid>
      <Grid
        className={classes.inputContainer}
        style={{ alignItems: 'flex-start' }}
        sx={{
          flexDirection: 'column',
        }}
      >
        <ReportCreateNewLabelField
          label={messages.SELECT_CATEGORY}
          component={
            <AutocompleteWithCheckBoxes
              options={extractCategory(categoryData)}
              handleChange={handleCategoryChange}
              disableClearAll={false}
            />
          }
          marginBottom="0px"
          marginRight="2.5rem"
        />
        {rows.length > 0 ? (
          <SortableTable
            columns={[
              { name: 'Parameter', width: '30%' },
              { name: 'Description', width: '60%' },
              { name: '', width: '' },
            ]}
            rows={rows}
            setRows={setRows}
            handleAddSubParameter={handleAddNewSubParameter}
            handleEdit={handleEditSubParameter}
            handleDelete={handleDeleteSubParameter}
            tableWidth="100%"
            rowOperationItems={<></>}
          />
        ) : (
          <NoDataVisual
            text={messages.NO_DATA_VISUAL}
            rootStyles={{
              height: 'auto',
              alignSelf: 'center',
              marginTop: '1rem',
            }}
          />
        )}
      </Grid>
      <Button
        variant="contained"
        label="Next"
        styleProps={{ marginLeft: 'auto' }}
        onClick={updateDueDiligenceDetails}
        disabled={rows.length === 0}
      />
    </Grid>
  );
};

const AddProjectTab: React.FC<AddProjectTabProps> = ({ currentTab }) => {
  switch (currentTab) {
    case 0:
      return <UploadDocket />;
    case 1:
      return <SelectParameters />;
    case 2:
      return (
        <OnBoardingPopUp
          title={messages.EXTRACT_DATA.TITLE}
          subTitle={messages.EXTRACT_DATA.SUBTITLE}
          statusTitles={[]}
          locateTitle={messages.EXTRACT_DATA.LINK_TITLE}
          locateLink={messages.EXTRACT_DATA.LINK}
        />
      );
    case 3:
      return <p>Data Ready for Review</p>;
    default:
      return <></>;
  }
};
const PageContents: React.FC<{}> = () => {
  const { classes } = useStyles();
  const cardStyles: CSSProperties = {
    borderRadius: '0.25rem',
    padding: '2rem',
  };
  const navigate = useNavigate();

  const breadcrumbsItemsList = [
    [
      'text',
      'M&A Due Diligence',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(MA_DUE_DILIGENCE);
      },
    ],
    [
      'text',
      'New Project',
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];
  const {
    currentStep,
    stepLoading,
    openEditModal,
    setOpenEditModal,
    selectedSubParameter,
    setSelectedSubParameter,
    selectedEditOptions,
    handleSubParameterSave,
  } = useAddProjectContext();

  const isSaveBtnDisabled =
    selectedSubParameter.name.length === 0 ||
    selectedSubParameter.value.length === 0;

  const handleModalClose = () => {
    setOpenEditModal(false);
    setSelectedSubParameter({
      name: '',
      value: '',
    });
  };

  const stepStatusTitles = messages.STEPS.map((step, index) => {
    const isCompleted = index < currentStep;
    const isActive = index === currentStep;

    return {
      title: step.title,
      icon: <step.Icon completed={isCompleted} highlighted={isActive} />,
      isCompleted,
      isActive,
    };
  });

  return (
    <>
      <Grid container>
        <Grid className={classes.breadcrumbs}>
          <Breadcrumbs itemList={breadcrumbsItemsList} />
        </Grid>

        <Grid className={classes.main}>
          <BlankCard
            body={
              <>
                <DocStepper statusTitles={stepStatusTitles} />
                <Grid className={classes.mainContent}>
                  {stepLoading ? (
                    <Loader />
                  ) : (
                    <AddProjectTab currentTab={currentStep} />
                  )}
                </Grid>
              </>
            }
            mainGridStyles={cardStyles}
          />
        </Grid>
      </Grid>
      <BasicModal
        open={openEditModal}
        handleClose={handleModalClose}
        title={
          (selectedEditOptions.mode === 'create' ? 'Add New ' : 'Edit ') +
          'Sub Parameter'
        }
        isSaveButton={true}
        saveBtnText="Save"
        saveBtnAction={handleSubParameterSave}
        saveBtnDisabled={isSaveBtnDisabled}
      >
        <AddSubParameter
          subParameterData={selectedSubParameter}
          setSubParameterData={setSelectedSubParameter}
        />
      </BasicModal>
    </>
  );
};

const MADueDiligenceAddProjectPage: React.FC<{}> = () => {
  return (
    <AddProjectProvider>
      <PageContents />
    </AddProjectProvider>
  );
};

export default MADueDiligenceAddProjectPage;
